import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plane, 
  MapPin, 
  Quote, 
  CheckCircle2, 
  ArrowRight,
  FileText,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { getSuccessStories } from '../services/apiService';

const VisaSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSuccessStories();
        // Filter for visa stories only
        const visaStories = data.filter(
          (s) => s.category === 'visa'
        );
        // Filter out stories with blob URLs
        const validStories = visaStories.filter(story => {
          const url = story.photo || story.photo_url || story.imageUrl;
          return !url || !url.startsWith('blob:');
        });
        setStories(validStories);
      } catch (error) {
        console.error("Failed to load visa stories", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Extract unique countries for filtering
  const countries = ['All', ...new Set(stories.map(s => {
    // Try to extract country from university field or use a default
    if (s.university) {
      // Simple extraction - you might want to improve this
      const countryMatch = s.university.match(/\b(USA|UK|Canada|Australia|Germany|France|Italy|Spain|Netherlands|Sweden|Norway|Denmark|Finland|New Zealand|Singapore|Japan|South Korea|China|India)\b/i);
      return countryMatch ? countryMatch[1] : null;
    }
    return null;
  }).filter(Boolean))];

  const filteredStories = filter === 'All' 
    ? stories 
    : stories.filter(s => {
      if (s.university) {
        const countryMatch = s.university.match(/\b(USA|UK|Canada|Australia|Germany|France|Italy|Spain|Netherlands|Sweden|Norway|Denmark|Finland|New Zealand|Singapore|Japan|South Korea|China|India)\b/i);
        return countryMatch && countryMatch[1] === filter;
      }
      return false;
    });

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* =========================================
          HERO SECTION
      ========================================= */}
      {/* Mobile Optimization: Reduced pt-32 to pt-24 for mobile to save vertical space */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg')] bg-repeat space-x-4"></div>
        <div className="absolute top-0 right-0 w-full h-full bg-red-900/10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-400 px-3 py-1 md:px-4 md:py-1.5 rounded-full text-xs md:text-sm font-bold uppercase tracking-wider mb-4 md:mb-6 mt-5">
              <CheckCircle2 size={14} className="md:w-4 md:h-4" /> 98% Visa Success Rate
            </div>
            
            {/* Mobile Optimization: text-3xl for mobile, text-5xl for desktop */}
            <h1 className="text-3xl md:text-5xl text-gray-100 font-serif font-bold mb-4 md:mb-6 leading-tight">
              Study Visa <span className="text-red-500">Success Stories</span>
            </h1>
            
            <p className="text-base md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed px-2">
              Turning Dreams into Global Opportunities
            </p>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          FILTER BAR (Sticky & Mobile Scrollable)
      ========================================= */}
      {/* Mobile Optimization: top-16 assuming a standard mobile navbar height */}
      <div className="top-16 md:top-20 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 py-3 md:py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
          {/* Visual gradient to hint scrolling on mobile */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent md:hidden pointer-events-none z-10" />
          
          <div className="flex flex-nowrap overflow-x-auto no-scrollbar gap-2 md:justify-center pb-1 snap-x scroll-pl-4">
            {countries.map(country => (
              <button
                key={country}
                onClick={() => setFilter(country)}
                className={`snap-start px-4 py-2 md:px-5 md:py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all flex-shrink-0 border ${
                  filter === country 
                    ? 'bg-slate-900 border-slate-900 text-white shadow-md' 
                    : 'bg-white border-slate-200 text-slate-600 hover:border-red-500 hover:text-red-600'
                }`}
              >
                {country}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* =========================================
          STORIES GRID
      ========================================= */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-red-600" size={48} />
            </div>
          ) : (
            <motion.div 
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              <AnimatePresence>
                {filteredStories.length > 0 ? filteredStories.map((story) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  key={story.id}
                  className="bg-white rounded-xl md:rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col"
                >
                  {/* Top: Profile Info */}
                  <div className="p-4 md:p-6 pb-0 flex items-start gap-3 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-slate-100 shadow-sm shrink-0 bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center">
                      {(() => {
                        const photoUrl = story.photo || story.photo_url || story.imageUrl || story.image;
                        const safePhotoUrl = photoUrl && !photoUrl.startsWith('blob:') ? photoUrl : null;
                        if (safePhotoUrl) {
                          return (
                            <img src={safePhotoUrl} alt={story.name || 'Student'} className="w-full h-full object-cover" />
                          );
                        }
                        return (
                          <span className="text-red-600 font-bold text-lg md:text-xl">{(story.name || 'S').charAt(0)}</span>
                        );
                      })()}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-bold text-slate-900 leading-tight truncate">{story.name || 'Student'}</h3>
                      <p className="text-xs md:text-sm text-slate-500 mt-1 line-clamp-1">{story.university || story.course || ''}</p>
                      
                      {story.university && (
                        <div className="flex items-center gap-1 text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-wider mt-1.5 md:mt-2">
                          <MapPin size={10} className="md:w-3 md:h-3 text-red-500"/> {story.university}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Middle: "Ticket" Info */}
                  <div className="p-4 md:p-6 flex-grow">
                    <div className="bg-slate-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-slate-100 relative overflow-hidden h-full">
                      <div className="absolute -right-4 -bottom-4 text-green-100 opacity-50 rotate-[-15deg] pointer-events-none">
                          <CheckCircle2 size={60} className="md:w-20 md:h-20" />
                      </div>
                      
                      <div className="relative z-10">
                        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-widest mb-1">Approved For</div>
                        <div className="font-serif font-bold text-slate-900 text-base md:text-lg mb-2 md:mb-3 leading-tight line-clamp-2">
                          {story.course || story.university || 'Student Visa'}
                        </div>
                        
                        <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-2 py-1 rounded text-[10px] md:text-xs font-bold border border-green-200">
                          <CheckCircle2 size={10} className="md:w-3 md:h-3" /> Student Visa Granted
                        </div>
                      </div>
                    </div>
                  </div>

                      {/* Bottom: Quote */}
                      <div className="px-4 md:px-6 pb-4 md:pb-6 mt-auto">
                        <div className="flex gap-2 mb-4">
                          <Quote size={16} className="text-slate-300 shrink-0 md:w-5 md:h-5" />
                          <p className="text-slate-600 italic text-xs md:text-sm leading-relaxed line-clamp-3">
                            {story.story || story.quote || 'Great experience with 6th Sense Training Academy!'}
                          </p>
                        </div>
                        
                        {story.visa_document_url && (
                          <a
                            href={story.visa_document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-all active:scale-95"
                          >
                            <FileText size={16} />
                            View Visa Document
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                </motion.div>
                )) : (
                  <div className="col-span-full text-center py-20">
                    <Plane size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No visa success stories found.</p>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* =========================================
          PROCESS VISUALIZATION (Timeline)
      ========================================= */}
      <section className="py-12 md:py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-start">
            
            {/* LEFT: TEXT CONTENT */}
            <div className="lg:sticky lg:top-32 mb-8 lg:mb-0">
              <span className="text-red-600 font-bold tracking-widest uppercase text-xs mb-3 block">
                Our Methodology
              </span>
              <h2 className="text-2xl md:text-5xl font-serif font-bold text-slate-900 mb-4 md:mb-6 leading-tight">
                We don't leave approvals to luck.
              </h2>
              <p className="text-base md:text-lg text-slate-600 mb-6 md:mb-8 leading-relaxed">
                Most rejections happen due to minor clerical errors or weak financial explanations. 
                <br className="hidden md:block" /><br className="hidden md:block" />
                At 6th Sense, every file passes through a <strong>4-Stage Senior Audit</strong> before it touches the embassy officer's desk.
              </p>
              
              <div className="bg-slate-50 border border-slate-100 p-4 md:p-6 rounded-2xl flex items-center gap-4">
                <div className="text-3xl md:text-4xl font-bold text-slate-900">0%</div>
                <div className="text-xs md:text-sm text-slate-500 font-medium uppercase tracking-wide">
                  Rejection rate for <br/> financial documentation
                </div>
              </div>
            </div>

            {/* RIGHT: THE INTERACTIVE TIMELINE */}
            <div className="relative pl-1 md:pl-0">
              
              {/* The Vertical Line: Adjusted positioning for Mobile (left-6) vs Desktop (left-8) to match circle centers */}
              <div className="absolute left-6 md:left-8 top-2 bottom-12 w-0.5 bg-gradient-to-b from-red-600 via-red-200 to-transparent"></div>

              <div className="space-y-10 md:space-y-12">
                {[
                  { id: '01', title: 'Financial Stress Testing', desc: 'We audit your bank statements, loan letters, and sponsorship proofs.' },
                  { id: '02', title: 'SOP Narrative Crafting', desc: 'Visa officers spend 2 minutes reading an SOP. We ensure your story hooks them.' },
                  { id: '03', title: 'Mock Embassy Interviews', desc: 'For USA/UK candidates, we conduct 3 rounds of mock interviews.' },
                  { id: '04', title: 'Precision Filing', desc: 'Final submission happens only after a "Triple-Check" by senior consultants.' }
                ].map((step) => (
                  <div key={step.id} className="relative flex items-start gap-4 md:gap-6 group">
                    {/* Mobile Optimization: Smaller circle w-12 vs w-16 */}
                    <div className="relative z-10 w-12 h-12 md:w-16 md:h-16 rounded-full bg-white border-2 border-red-100 text-red-600 flex items-center justify-center text-base md:text-xl font-bold shadow-sm group-hover:bg-red-600 group-hover:text-white group-hover:border-red-600 transition-all duration-300 flex-shrink-0">
                      {step.id}
                    </div>
                    <div className="pt-1 md:pt-2">
                      <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-1 md:mb-2 font-serif">{step.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}

                {/* FINAL SUCCESS BADGE */}
                <div className="relative flex items-center gap-4 md:gap-6 pt-2">
                  <div className="relative z-10 w-12 h-12 md:w-16 md:h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center shadow-lg shadow-green-200 flex-shrink-0">
                    <CheckCircle2 size={24} className="md:w-8 md:h-8" />
                  </div>
                  <div>
                    <div className="text-green-700 font-bold text-base md:text-lg uppercase tracking-wider">Visa Approved</div>
                    <div className="text-xs text-slate-400 font-medium">Ready for departure</div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          CTA: SUBMIT PROFILE
      ========================================= */}
      <section className="py-16 md:py-24 bg-slate-900 text-white border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-block p-3 md:p-4 rounded-full bg-red-600/10 mb-4 md:mb-6">
            <Plane size={24} className="md:w-8 md:h-8 text-red-500 animate-pulse" />
          </div>
          <h2 className="text-2xl md:text-3xl text-gray-100 font-serif font-bold mb-4 md:mb-6">
            Your name belongs on this list.
          </h2>
          <p className="text-slate-400 text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto">
            Don't risk a refusal. Let our senior visa consultants review your profile before you submit.
          </p>
          <Link
            to="/book-free-counselling"
            className="w-full md:w-auto inline-flex justify-center items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg text-base md:text-lg font-bold transition-all hover:scale-105 shadow-xl shadow-red-900/20"
          >
            Get Free Profile Assessment <ArrowRight size={20} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default VisaSuccessStories;